import { EstadoCanvas } from '../dominio/assistente.interface'
import type { ClienteHttpAssistente } from './assistantClient'

interface EntradaSalvamentoFarol {
  cliente: ClienteHttpAssistente
  sessaoId: string
  interacaoId: string
  canvas: EstadoCanvas
}

interface RespostaSalvamentoFarol {
  estado: 'concluida' | 'concluida_reutilizada'
  navegacao?: {
    versao_contrato: '2.0'
    tipo: 'navegacao'
    rotulo: string
    identificador_rota: 'abrir_graficos_salvos_no_farol_do_dp'
    rota_autorizada: '/farol-do-dp-2/meus-graficos'
    modo_de_abertura: 'handoff'
  }
}

export function chaveDoSalvamentoNoFarol (sessaoId: string, interacaoId: string): string {
  return JSON.stringify([sessaoId, interacaoId])
}

function textoPreenchido (valor: unknown): valor is string {
  return typeof valor === 'string' && Boolean(valor.trim())
}

function possuiChavesExatas (valor: Record<string, unknown>, chaves: string[]): boolean {
  return Object.keys(valor).length === chaves.length && chaves.every(chave => chave in valor)
}

function handleValido (canvas: EstadoCanvas): canvas is Extract<EstadoCanvas, { type: 'chart' | 'charts' }> & {
  artefatoId: string
  expiraEm: string
} {
  if ((canvas.type !== 'chart' && canvas.type !== 'charts') ||
    !textoPreenchido(canvas.artefatoId) ||
    !textoPreenchido(canvas.expiraEm)) return false
  const expiracao = Date.parse(canvas.expiraEm)
  return Number.isFinite(expiracao) && expiracao > Date.now()
}

export function canvasPodeSerSalvoNoFarol (canvas: EstadoCanvas): boolean {
  return handleValido(canvas)
}

function respostaValida (valor: unknown): valor is RespostaSalvamentoFarol {
  if (typeof valor !== 'object' || valor === null || Array.isArray(valor)) return false
  const resposta = valor as Record<string, unknown>
  const chavesEsperadas = resposta.navegacao === undefined
    ? ['estado']
    : ['estado', 'navegacao']
  if (!possuiChavesExatas(resposta, chavesEsperadas) ||
    (resposta.estado !== 'concluida' && resposta.estado !== 'concluida_reutilizada')) return false
  if (resposta.navegacao === undefined) return true
  if (typeof resposta.navegacao !== 'object' || resposta.navegacao === null ||
    Array.isArray(resposta.navegacao)) return false
  const navegacao = resposta.navegacao as Record<string, unknown>
  return possuiChavesExatas(navegacao, [
    'versao_contrato',
    'tipo',
    'rotulo',
    'identificador_rota',
    'rota_autorizada',
    'modo_de_abertura'
  ]) &&
    navegacao.versao_contrato === '2.0' &&
    navegacao.tipo === 'navegacao' &&
    textoPreenchido(navegacao.rotulo) &&
    navegacao.identificador_rota === 'abrir_graficos_salvos_no_farol_do_dp' &&
    navegacao.rota_autorizada === '/farol-do-dp-2/meus-graficos' &&
    navegacao.modo_de_abertura === 'handoff'
}

export async function salvarGraficoNoFarol (
  entrada: EntradaSalvamentoFarol
): Promise<RespostaSalvamentoFarol> {
  if (!textoPreenchido(entrada.sessaoId) || !textoPreenchido(entrada.interacaoId) ||
    !handleValido(entrada.canvas)) throw new Error('grafico_farol_invalido')
  const rota = `/assistente/v2/sessoes/${encodeURIComponent(entrada.sessaoId)}` +
    `/interacoes/${encodeURIComponent(entrada.interacaoId)}` +
    '/artefatos-graficos/salvar-no-farol-do-dp'
  let dados: unknown
  try {
    dados = (await entrada.cliente.post<unknown>(rota, {
      artefato_id: entrada.canvas.artefatoId
    })).data
  } catch {
    throw new Error('salvamento_farol_falhou')
  }
  if (!respostaValida(dados)) throw new Error('resposta_farol_invalida')
  return dados
}
